import { ExtraProductByTypeMap, Pagination, PaginationArgsType, ProductFilterType, ProductOfCategory, ProductType } from '@/@types';
import { gql } from './_utils';
import { NamespaceArgsType } from './@types';

type ProductOptionsType = { filters?: ProductFilterType; pagination?: PaginationArgsType };

const PRODUCT_DETAILS_FRAGMENT = gql`
  fragment ProductDetails on Product {
    id
    name
    image
    description
    popularity
    media {
      type
      source
    }
    category {
      id
      name
    }
    subcategories {
      label
      name
    }
    recommendations(pagination: $recommendationsPagination, filters: $recommendationFilters) {
      total
    }
    extraInfo {
      ratings {
        source
        value
        unit
        total
        votes
      }
      ... on Restaurant {
        externalId {
          covermanager
        }
        workingHours {
          weekDay
          times {
            from
            to
          }
        }
        location {
          fullAddress
          geo {
            lat
            lng
          }
        }
        phone
        price
        url
      }
      ... on Movie {
        preview {
          source
          id
        }
        releaseDate
        providers {
          id
          name
          url
        }
      }
      ... on Serie {
        preview {
          source
          id
        }
        releaseDate
        providers {
          id
          name
          url
        }
      }
      ... on Place {
        location {
          fullAddress
          geo {
            lat
            lng
          }
        }
        workingHours {
          weekDay
          times {
            from
            to
          }
        }
        phone
        price
        url
      }
      ... on Music {
        album
        duration
        artists {
          id
          name
        }
        providers {
          name
          url
        }
      }
    }
  }
`;

export const Product = ({ graphqlRequest }: NamespaceArgsType) => {
  return {
    get: async (id: string) => {
      return graphqlRequest<{ Product: { get?: ProductType } }>({
        query: gql`
          ${PRODUCT_DETAILS_FRAGMENT}
          query getProduct($id: ID!, $recommendationsPagination: PaginationStringArgs, $recommendationFilters: ProductRecommendationFilters) {
            Product {
              get(id: $id) {
                ...ProductDetails
                related {
                  id
                  name
                  media {
                    type
                    source
                  }
                  category {
                    id
                    name
                  }
                }
                recommendations(pagination: $recommendationsPagination, filters: $recommendationFilters) {
                  edges {
                    node {
                      id
                      message
                      createdAt
                      user {
                        id
                        nickname
                        avatar
                        level {
                          id
                          name
                        }
                      }
                      likes {
                        total
                      }
                    }
                  }
                }
              }
            }
          }
        `,
        variables: {
          id,
          recommendationFilters: { withMessage: true },
        },
      }).then((res) => {
        return res.data.Product.get;
      });
    },
    find: async <T extends keyof ExtraProductByTypeMap = any>({ filters, pagination = { first: 10 } }: ProductOptionsType) => {
      return graphqlRequest<{ Product: { find: Pagination<ProductOfCategory<T>> } }>({
        query: gql`
          query findProduct($filters: ProductFilter, $pagination: PaginationStringArgs) {
            Product {
              find(filters: $filters, pagination: $pagination) {
                edges {
                  node {
                    id
                    name
                    image
                    media {
                      type
                      source
                    }
                    recommendations(pagination: { first: 1 }, filters: { withMessage: true }) {
                      edges {
                        node {
                          message
                          user {
                            nickname
                            avatar
                            level {
                              name
                              id
                            }
                          }
                        }
                      }
                    }
                    category {
                      id
                      name
                    }
                    popularity
                    extraInfo {
                      ... on Movie {
                        releaseDate
                      }
                      ... on Serie {
                        releaseDate
                      }
                      ... on Restaurant {
                        phone
                        price
                        url
                        externalId {
                          covermanager
                        }
                        location {
                          geo {
                            lat
                            lng
                          }
                          fullAddress
                        }
                        ratings {
                          value
                          source
                          votes
                          total
                          unit
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `,
        variables: { filters, pagination },
      });
    },
  };
};
